import { useQuery } from '@tanstack/react-query'
import { useBankGates } from '~/features/bank/hooks/useBankGates'

type StoreAccess = {
    allowed: boolean
    reason: string | null
}

/**
 * Whether this account may buy, and the sentence to show when it may not.
 *
 * Four things can refuse a purchase -- a freeze, a store block, the trust gate and the integrity
 * gate -- and until this hook the client knew about exactly one of them. The other three refused
 * server-side into a client that could not read a failure, so the player was shown "Thank you for
 * your purchase!" for an order that was never created. The button is greyed here so nobody is
 * walked through three checkout steps to be told no at the end.
 *
 * `/api/store/access` calls the same `refusal_reason` the order endpoint raises on, so this cannot
 * drift from what actually happens on submit.
 *
 * One `queryKey` for the whole page: react-query dedupes, so a grid of fifty cards each calling
 * this costs one request, not fifty.
 */
export const useStoreGate = () => {
    // The freeze, straight off the cached viewer. Held alongside the fetch rather than replaced by
    // it so a frozen account sees its reason on first paint instead of a live button that turns
    // dead a moment later.
    const { spendBlocked } = useBankGates()

    const { data, isLoading } = useQuery<StoreAccess>({
        queryKey: ['store-access'],
        queryFn: async () => {
            const response = await fetch('/api/store/access')
            if (!response.ok) {
                // Fail open, exactly as every one of these gates does server-side: a review
                // feature that cannot be reached must not close the shop. The purchase itself is
                // still gated, so nothing is let through that the server would refuse.
                return { allowed: true, reason: null }
            }
            return await response.json()
        },
        retry: false,
        staleTime: 1000 * 60,
    })

    return {
        /** The reason to show, or undefined when this account may buy. */
        blockedReason: spendBlocked ?? data?.reason ?? undefined,
        isLoading,
    }
}
